import { Component, OnInit, Output, EventEmitter, ViewChild, ChangeDetectorRef } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { AuthService } from "src/shared/Services/auth.service";

import { Unsubscriber } from "@shared/Components/Unsubscriber/unsubscriber.component";
import { ModalDashboardSelectComponent } from "@shared/Components/UserDashboardModalSelect/modal-dashboard-select.component";
import { Usuario } from "@shared/Entities/Usuario/usuario.entity";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";
import { CacheService } from "@shared/Services/cache.service";

import { UnitSharedService } from "@Unit/shared/services/unitshared.service";

import { UserProduct } from "../../shared/Interfaces/user-products.interface";
import { SelectProfileDropdownComponent } from "../Perfiles/Components/select-profile-dropdown/select-profile-dropdown.component";
import { SelectProfileModalComponent } from "../Perfiles/Components/select-profile-modal/select-profile-modal.component";

import { HeaderRepository } from "./header.repository";
import { HeaderService } from "./header.service";
import { MenuSection } from "./Types/menu.interface";
import { RouteData } from "./Types/route-data.interface";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent extends Unsubscriber implements OnInit {
  public currentUser: Usuario;
  public showStatusBar: boolean = true;
  public isUnitPortalStyle: boolean = false;
  public openedMenuSection: string;
  public openedSubMenuSection: string;
  public sideMenu = false;
  public menu: MenuSection[]; // Menú actual
  private originalMenu: MenuSection[]; // Menú original sin modificar
  public routesData$: Observable<RouteData[]>;
  public bsModalRef: BsModalRef;
  public selectedProfile: number;
  public profileListResponse: unknown;
  public products: UserProduct[] = [];
  public isLoaded = false;

  @ViewChild(SelectProfileDropdownComponent, { static: false }) public dropdownComponent: SelectProfileDropdownComponent;
  @Output() public sidebarToggled: EventEmitter<void> = new EventEmitter();

  constructor(
    private cdRef: ChangeDetectorRef,
    protected usuarioRepository: UsuariosRepository,
    private headerService: HeaderService,
    public headerRepository: HeaderRepository,
    private authService: AuthService,
    private modalService: BsModalService,
    private cacheService: CacheService,
    private router: Router,
    private unitSharedService: UnitSharedService
  ) {
    super();
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.showStatusBar = this.router.url !== "/portales";
      this.hideSideMenu();
      this.updateMenu(); // Actualizar el menú en cada cambio de URL
    });
  }

  public ngOnInit(): void {
    // Establecer el portalStyle desde sessionStorage
    this.setCurrentClient();
    this.routesData$ = this.headerService.routesData$;
    this.loadMenu(); // Inicializar la carga del menú original
    this.unitSharedService.isUnitPortalStyle$.subscribe((isUnit) => {
      this.isUnitPortalStyle = isUnit;
    });
  }

  private loadMenu(): void {
    // Método para cargar y almacenar el menú original
    this.headerService.getCurrentUserMenu().subscribe((newMenu) => {
      this.originalMenu = JSON.parse(JSON.stringify(newMenu)).filter((x) => x.id !== "seccion-personal-pagos"); // Guardar el menú original como una copia profunda
      this.updateMenu(); // Inicializar el menú actual con el original
    });
  }
  private updateMenu(): void {
    let filteredMenu: Array<MenuSection> = this.originalMenu ? JSON.parse(JSON.stringify(this.originalMenu)) : [];
    const sectionsToMove = [];

    if (this.isUnitPortalStyle) {
      filteredMenu = this.filterMenuForUnitPortal(filteredMenu);
    }

    filteredMenu.forEach((section) => {
      this.updateSectionName(section);
      this.collectSectionsToMove(section, sectionsToMove, filteredMenu);
    });

    this.menu = [...filteredMenu, ...sectionsToMove];
    this.isLoaded = true;
    this.cdRef.detectChanges();
  }

  private updateSectionName(section: MenuSection): void {
    if (section.name === "Riesgos Generales") {
      section.name = this.isUnitPortalStyle ? "SEGUROS UNIVERSAL" : this.getOriginalSectionName(section);
    }
  }

  private getOriginalSectionName(section: MenuSection): string {
    const originalSection = this.originalMenu.find((origSection) => origSection.id === section.id);
    return originalSection ? originalSection.name : section.name;
  }

  private collectSectionsToMove(section: MenuSection, sectionsToMove: MenuSection[], filteredMenu: MenuSection[]): void {
    if (
      (this.isUnitPortalStyle && section.name === "SEGUROS UNIVERSAL") ||
      (!this.isUnitPortalStyle && section.name === "SEGUROS UNIT")
    ) {
      // Eliminar la sección de su posición actual
      const index = filteredMenu.indexOf(section);
      if (index > -1) {
        filteredMenu.splice(index, 1);
      }
      // Añadir la sección al final
      sectionsToMove.push(section);
    }
  }

  private filterMenuForUnitPortal(menu: Array<MenuSection>): Array<MenuSection> {
    return menu.filter((item) => item.portal === "unit" || item.id === "home" || item.id === "contacto" || item.id === "riesgos");
  }

  private redirectLogOutByAfiliate(): void {
    if (this.isUnitPortalStyle) {
      this.router.navigateByUrl("/unit");
    }
  }

  public getLogoClass(): string {
    return this.isUnitPortalStyle ? "unit-logo" : "universal-logo";
  }

  public getLogoSrc(): string {
    return this.isUnitPortalStyle ? "assets/images/unit-desktop-logo.svg" : "assets/images/universal-logo.svg";
  }

  public getSelectedProfileName(): string {
    const profile = this.currentUser.usuarioPerfil.find((p) => p.perfilId === this.currentUser.perfilId);
    return profile ? profile.perfil.nombre : "";
  }

  public onOptionSelected(): void {
    if (this.dropdownComponent) this.dropdownComponent.showDropdown = false;
    this.anotherSubscription = this.usuarioRepository.switchProfile(this.selectedProfile).subscribe((userData) => {
      this.currentUser = userData;
      localStorage.setItem("currentProfile", userData?.perfilId);
      this.selectedProfile = userData.perfilId;
      window.location.reload();
    });
  }

  public openSelectProfileModal(): void {
    this.bsModalRef = this.modalService.show(SelectProfileModalComponent, {
      class: "modal-md",
      initialState: {
        title: "Selección de Perfil",
        text: "Inicia sesión con tu perfil personalizado. Cada perfil tiene un tablero exclusivo diseñado para ti",
        profileList: this.currentUser.usuarioPerfil,
        perfilId: this.selectedProfile,
        showInstructions: false,
      },
    });

    this.anotherSubscription = this.bsModalRef.content.selectedProfile.subscribe((selectedProfileId: number) => {
      this.selectedProfile = selectedProfileId;
      this.bsModalRef.content.showInstructions = true;
    });

    this.bsModalRef.onHidden.subscribe(() => {
      this.onOptionSelected();
    });
  }

  public setCurrentClient(): void {
    this.anotherSubscription = this.usuarioRepository.getInfoClienteActual().subscribe((currentUser) => {
      this.currentUser = currentUser;
      if (currentUser.usuarioPerfil.length < 2) return;
      const currentProfile = localStorage.getItem("currentProfile");
      if (currentProfile) {
        this.selectedProfile = Number(currentProfile);
      } else {
        this.selectedProfile = currentUser.perfilId;
        this.openSelectProfileModal();
      }
      if (!this.currentUser?.usuarioRole.length) {
        this.router.navigateByUrl("/unathorized");
      }
    });
  }

  public onSidebarToggled(): void {
    this.sidebarToggled.emit();
  }

  public showSideMenu(): void {
    this.sideMenu = true;
  }

  public hideSideMenu(): void {
    this.sideMenu = false;
  }

  public goBack(): void {
    this.router.navigate([this.router.url.substr(0, this.router.url.lastIndexOf("/"))]);
  }

  public signOut(): void {
    this.anotherSubscription = this.authService.logout().subscribe(() => {
      this.sideMenu = false;
      localStorage.setItem("portalAction", "logout");
      this.redirectLogOutByAfiliate();
      this.cacheService.remove("currentProfile");
    });
  }

  public resetPassword(): void {
    this.authService.resetPassword();
  }

  public onChangeOpenedMenuSection(section: string): void {
    this.openedMenuSection = this.openedMenuSection === section ? "" : section;
  }

  public onChangeOpenedSubMenuSection(subSection: string): void {
    this.openedSubMenuSection = this.openedSubMenuSection === subSection ? "" : subSection;
  }

  public verifyCpDashboardSelection(): void {
    const selectedProductDashboard = localStorage.getItem("selectedDashboard");
    if (selectedProductDashboard) {
      this.router.navigateByUrl(selectedProductDashboard);
    } else {
      this.usuarioRepository.getUserProducts().subscribe((products) => {
        this.products = products;
        if (this.products && this.products.length > 1) {
          this.openDashboardSelectModal();
        } else {
          localStorage.setItem("selectedDashboard", this.products[0].url);
          this.router.navigateByUrl(this.products[0].url);
        }
      });
    }
  }

  public openDashboardSelectModal(): void {
    localStorage.setItem("dashboardModalOpen", "true");
    this.bsModalRef = this.modalService.show(ModalDashboardSelectComponent, {
      class: "modal-md",
      initialState: {
        OptionArray: this.products,
      },
    });
  }

  public getHeaderByCompany(): string {
    return this.isUnitPortalStyle ? "m-unit-header-bar" : "m-header-bar";
  }

  public getNavigationMenu(): string {
    return this.isUnitPortalStyle ? "text-primary-unit" : "text-info";
  }
}
